import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';

export interface SettlementLevel {
  id: string;
  name: string;
  startingChips: number;
  initialCounts: Record<string, number>;
  raCounts: Record<string, number>;
  activityFee1: number;
  prizeContribution: number;
  participationPoints: number;
  rankPoints: number[];
}

type LevelInput = Omit<SettlementLevel, 'id'>;
export function editableLevel(level: SettlementLevel): LevelInput {
  return {
    name: level.name,
    startingChips: level.startingChips,
    initialCounts: { ...level.initialCounts },
    raCounts: { ...level.raCounts },
    activityFee1: level.activityFee1,
    prizeContribution: level.prizeContribution,
    participationPoints: level.participationPoints,
    rankPoints: [...(level.rankPoints ?? [])],
  };
}
const faces = [100, 500, 2500, 10000, 50000];
const blank = (): LevelInput => ({
  name: '',
  startingChips: 0,
  initialCounts: Object.fromEntries(faces.map((face) => [face, 0])),
  raCounts: Object.fromEntries(faces.map((face) => [face, 0])),
  activityFee1: 0,
  prizeContribution: 0,
  participationPoints: 0,
  rankPoints: [3, 2, 1],
});

export const parseRankPoints = (text: string): number[] | null => {
  const parts = text
    .split(/[,，\s]+/)
    .filter(Boolean)
    .map(Number);
  return parts.every((n) => Number.isSafeInteger(n) && n >= 0) && parts.length <= 50 ? parts : null;
};

export function useSettlementLevels() {
  return useQuery({
    queryKey: ['timed-settlement-levels'],
    queryFn: () => api.get<SettlementLevel[]>('/tournament/timed-settlement-levels'),
  });
}

export default function TimedSettlementLevelsPanel() {
  const { t } = useTranslation('tournament');
  const allowedActions = useAuthStore((state) => state.allowedActions);
  const canCreate = allowedActions?.includes('template:create') ?? false;
  const canUpdate = allowedActions?.includes('template:update') ?? false;
  const qc = useQueryClient();
  const { data: levels = [] } = useSettlementLevels();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<LevelInput>(blank);
  const [error, setError] = useState('');
  const [rankText, setRankText] = useState('3, 2, 1');
  const rankPoints = parseRankPoints(rankText);
  const denominations = [
    ...new Set([...Object.keys(form.initialCounts), ...Object.keys(form.raCounts)].map(Number)),
  ].sort((a, b) => a - b);
  const initialTotal = denominations.reduce(
    (sum, face) => sum + face * (form.initialCounts[String(face)] ?? 0),
    0,
  );
  const raTotal = denominations.reduce(
    (sum, face) => sum + face * (form.raCounts[String(face)] ?? 0),
    0,
  );
  const save = useMutation({
    mutationFn: () => {
      const body = { ...form, startingChips: initialTotal, rankPoints: rankPoints ?? [] };
      return editingId
        ? api.patch(`/tournament/timed-settlement-levels/${editingId}`, body)
        : api.post('/tournament/timed-settlement-levels', body);
    },
    onSuccess: () => {
      setEditingId(null);
      setForm(blank());
      setRankText('3, 2, 1');
      setError('');
      void qc.invalidateQueries({ queryKey: ['timed-settlement-levels'] });
    },
    onError: (failure: Error) => setError(failure.message),
  });
  const edit = (level: SettlementLevel) => {
    setEditingId(level.id);
    setForm(editableLevel(level));
    setRankText((level.rankPoints ?? []).join(', '));
  };
  const changeCount = (kind: 'initialCounts' | 'raCounts', face: number, value: number) => {
    setForm((current) => ({
      ...current,
      [kind]: { ...current[kind], [face]: value },
    }));
  };
  const showEditor = editingId ? canUpdate : canCreate;
  return (
    <section className="rounded-xl border border-base-300 p-4 mb-6 space-y-4">
      <h1 className="font-semibold">{t('timedLevels.title')}</h1>
      <p className="text-sm text-base-content/70">{t('timedLevels.description')}</p>
      <div className="flex flex-wrap gap-2">
        {levels.map((level) => {
          const label = t('timedLevels.levelSummary', {
            name: level.name,
            chips: level.startingChips.toLocaleString(),
          });
          return canUpdate ? (
            <button
              key={level.id}
              type="button"
              className="rounded border px-3 py-2"
              onClick={() => edit(level)}
            >
              {label}
            </button>
          ) : (
            <span key={level.id} className="rounded border px-3 py-2">
              {label}
            </span>
          );
        })}
        {canCreate && (
          <button
            type="button"
            className="rounded border px-3 py-2"
            onClick={() => {
              setEditingId(null);
              setForm(blank());
              setRankText('3, 2, 1');
            }}
          >
            {t('timedLevels.add')}
          </button>
        )}
      </div>
      {showEditor && (
        <>
          <div className="grid gap-3 sm:grid-cols-5">
            {(
              [
                ['name', t('timedLevels.name')],
                ['activityFee1', t('timedLevels.activityFee1')],
                ['prizeContribution', t('timedLevels.prizeContribution')],
                ['participationPoints', t('timedLevels.participationPoints')],
              ] as const
            ).map(([key, label]) => (
              <label key={key} className="text-sm">
                {label}
                <input
                  className="mt-1 w-full rounded border p-2"
                  type={key === 'name' ? 'text' : 'number'}
                  min={key === 'name' ? undefined : 0}
                  value={form[key]}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      [key]: key === 'name' ? event.target.value : Number(event.target.value),
                    })
                  }
                />
              </label>
            ))}
            <label className="text-sm">
              {t('timedLevels.rankPoints')}
              <input
                className="mt-1 w-full rounded border p-2"
                type="text"
                inputMode="numeric"
                value={rankText}
                aria-invalid={rankPoints === null}
                onChange={(event) => setRankText(event.target.value)}
              />
              <span className="text-xs text-base-content/60">
                {t('timedLevels.rankPointsHint')}
              </span>
            </label>
          </div>
          <p className="my-2 text-sm text-muted-foreground">
            {t('timedLevels.netContribution', {
              gross: form.prizeContribution,
              fee: form.activityFee1,
              net: form.prizeContribution - form.activityFee1,
            })}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th>{t('timedLevels.denomination')}</th>
                  <th>{t('timedLevels.initialCounts')}</th>
                  <th>{t('timedLevels.raCounts')}</th>
                </tr>
              </thead>
              <tbody>
                {denominations.map((face) => (
                  <tr key={face}>
                    <th>{face.toLocaleString()}</th>
                    {(['initialCounts', 'raCounts'] as const).map((kind) => (
                      <td key={kind}>
                        <input
                          className="w-full rounded border p-2"
                          type="number"
                          min="0"
                          step="1"
                          value={form[kind][String(face)] ?? 0}
                          onChange={(event) => changeCount(kind, face, Number(event.target.value))}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!denominations.includes(100000) && (
            <button
              type="button"
              className="rounded border px-3 py-2"
              onClick={() =>
                setForm((current) => ({
                  ...current,
                  initialCounts: { ...current.initialCounts, '100000': 0 },
                  raCounts: { ...current.raCounts, '100000': 0 },
                }))
              }
            >
              {t('timedLevels.addDenomination')}
            </button>
          )}
          <p className="text-sm">
            {t('timedLevels.totals', {
              initial: initialTotal.toLocaleString(),
              ra: raTotal.toLocaleString(),
            })}
          </p>
          <button
            type="button"
            className="rounded bg-primary px-4 py-2 text-primary-content disabled:opacity-50"
            disabled={
              save.isPending ||
              rankPoints === null ||
              !form.name.trim() ||
              initialTotal <= 0 ||
              form.prizeContribution <= form.activityFee1
            }
            onClick={() => save.mutate()}
          >
            {editingId ? t('timedLevels.update') : t('timedLevels.save')}
          </button>
          {error && (
            <p role="alert" className="text-error text-sm">
              {error}
            </p>
          )}
        </>
      )}
    </section>
  );
}
